import { ShieldAlert, Layers, Fingerprint, Route, Scale, BrainCircuit, Activity, FileCheck, Lock, ShieldCheck, TrendingDown, IndianRupee, Handshake, Sprout, BadgeCheck } from "lucide-react";
import { PROBLEMS, SERVICE_GROUPS, BUSINESS_IMPACT, INTELLIGENCE, STEPS, DIFF_ROWS, TECH_LINE } from "@/lib/data";
import { SectionHead, Reveal, Card, Tag, TextLink, Eyebrow } from "@/components/primitives";

const ICONS = { ShieldAlert, Layers, Fingerprint, Route, Scale, BrainCircuit, Activity, FileCheck, Lock, ShieldCheck };
const IMPACT_ICONS = [TrendingDown, IndianRupee, Handshake, Sprout, BadgeCheck];

export function TrustStrip() {
  const pillars = ["Compliance-First", "AI-Driven", "Privacy by Design", "Secure by Design", "Bharat-First"];
  return (
    <section className="border-b border-line py-20 sm:py-24" data-testid="trust-strip">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-8">
        <SectionHead
          eyebrow="Trust Architecture"
          title="Trust Isn't a Feature. It's the Architecture."
        />
        <Reveal delay={0.1} className="mt-10 flex flex-wrap gap-3">
          {pillars.map((p) => (
            <span key={p} data-testid={`trust-pillar-${p.toLowerCase().replace(/\s+/g, "-")}`} className="rounded-full border border-line bg-panel/70 px-5 py-2.5 font-display text-sm font-semibold text-mist transition-colors duration-300 hover:border-teal/50 hover:text-teal">
              {p}
            </span>
          ))}
        </Reveal>
        <Reveal delay={0.2} className="mt-8">
          <TextLink to="/trust-center" testid="trust-strip-cta">Explore the Trust Center</TextLink>
        </Reveal>
      </div>
    </section>
  );
}

export function ProblemCards() {
  return (
    <section className="border-b border-line py-24 sm:py-32" data-testid="problem-section">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-8">
        <SectionHead
          eyebrow="Chapter 01 — The Gap"
          title="Digital Finance Is Growing. So Are the Risks."
          lead="India's digital financial ecosystem is becoming faster, more connected and more accessible — but fraud, identity misuse, privacy risks and fragmented compliance are creating a growing trust & risk gap."
        />
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
          {PROBLEMS.map((p, i) => {
            const Icon = ICONS[p.icon];
            return (
              <Reveal key={p.title} delay={i * 0.07}>
                <Card testid={`problem-card-${i}`} className="group h-full p-6">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-line bg-abyss text-electric transition-colors duration-300 group-hover:border-electric/50">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 font-display text-base font-semibold text-mist">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-mute">{p.body}</p>
                </Card>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function ServiceGroups() {
  return (
    <section className="border-b border-line bg-abyss/50 py-24 sm:py-32" data-testid="solution-section">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-8">
        <SectionHead
          eyebrow="Chapter 02 — The Platform"
          title="One Intelligent Infrastructure. Multiple Financial Journeys."
          lead="TimePe is being built as an API-led financial infrastructure platform that brings payments, banking, insurance and financial access together with risk intelligence, identity, behavior, transactions, fraud intelligence, privacy, security and compliance at its core into one real-time decisioning framework."
        />
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {SERVICE_GROUPS.map((g, i) => (
            <Reveal key={g.title} delay={i * 0.07}>
              <Card testid={`service-group-${g.title.toLowerCase()}`} className="flex h-full flex-col p-7">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-mono text-xs uppercase tracking-[0.24em] text-electric">{g.title}</h3>
                  <Tag tone={g.tone}>{g.status}</Tag>
                </div>
                <ul className="mt-6 space-y-3">
                  {g.items.map((item) => (
                    <li key={item} className="flex items-center gap-3 text-sm text-mist/85">
                      <span className="h-1 w-1 rounded-full bg-teal" aria-hidden="true" />
                      {item}
                    </li>
                  ))}
                </ul>
              </Card>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15} className="mt-12">
          <div className="rounded-2xl border border-line bg-panel/70 p-7">
            <Eyebrow>Business Impact</Eyebrow>
            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
              {BUSINESS_IMPACT.map((b, i) => {
                const Icon = IMPACT_ICONS[i];
                return (
                  <div key={b} data-testid={`impact-item-${i}`} className="flex items-center gap-3">
                    <Icon className="h-4.5 w-4.5 h-5 w-5 shrink-0 text-teal" />
                    <span className="text-sm font-medium text-mist/90">{b}</span>
                  </div>
                );
              })}
            </div>
            <p className="mt-6 text-xs leading-relaxed text-mute/80">
              Services roll out in phases. Tags distinguish in-development, planned and future capabilities.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function IntelligenceCards({ showCta = true }) {
  return (
    <section className="theme-dark border-b border-line bg-ink py-24 sm:py-32" data-testid="intelligence-section">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-8">
        <SectionHead
          eyebrow="Chapter 03 — Intelligence"
          title="The Intelligence Layer Behind Financial Trust."
          lead="TimePe goes beyond transaction processing. Its technology vision is to build intelligence around financial activity — helping identify risk, detect anomalies, strengthen compliance and improve digital trust."
        />
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
          {INTELLIGENCE.map((c, i) => {
            const Icon = ICONS[c.icon];
            return (
              <Reveal key={c.title} delay={i * 0.07}>
                <Card testid={`intelligence-card-${i}`} className="group relative h-full overflow-hidden p-6">
                  <span className="pointer-events-none absolute -right-10 -top-10 h-28 w-28 rounded-full bg-glow-teal opacity-0 transition-opacity duration-500 group-hover:opacity-100" aria-hidden="true" />
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-line bg-abyss text-teal transition-colors duration-300 group-hover:border-teal/50">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 font-display text-base font-semibold text-mist">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-mute">{c.body}</p>
                </Card>
              </Reveal>
            );
          })}
        </div>
        {showCta && (
          <Reveal delay={0.2} className="mt-10">
            <TextLink to="/technology" testid="intelligence-cta">Explore Our Technology</TextLink>
          </Reveal>
        )}
      </div>
    </section>
  );
}

export function HowItWorks() {
  return (
    <section className="border-b border-line bg-abyss/50 py-24 sm:py-32" data-testid="how-it-works">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-8">
        <SectionHead
          eyebrow="Chapter 04 — The Flow"
          title="From Financial Access to Financial Intelligence"
        />
        <div className="relative mt-16">
          <div className="absolute left-0 right-0 top-7 hidden h-px bg-gradient-to-r from-electric/40 via-teal/40 to-electric/40 lg:block" aria-hidden="true" />
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <Reveal key={s.num} delay={i * 0.1}>
                <div data-testid={`step-${s.num}`} className="relative">
                  <span className="relative z-10 flex h-14 w-14 items-center justify-center rounded-full border border-electric/40 bg-ink font-mono text-sm text-electric">
                    {s.num}
                  </span>
                  <h3 className="mt-6 font-display text-xl font-semibold text-mist">{s.title}</h3>
                  <p className="mt-3 max-w-xs text-sm leading-relaxed text-mute">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function Differentiation() {
  return (
    <section className="border-b border-line py-24 sm:py-32" data-testid="differentiation-section">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-8">
        <SectionHead eyebrow="Chapter 05 — The Difference" title="Built Different. Built for Bharat." />
        <Reveal className="mt-14 overflow-hidden rounded-2xl border border-line">
          <div className="grid grid-cols-2 bg-panel2/80">
            <p className="px-6 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-mute">Traditional Approach</p>
            <p className="border-l border-line px-6 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-electric">The TimePe Way</p>
          </div>
          <div className="md:hidden">
            {DIFF_ROWS.map(([left, right], i) => (
              <div key={i} data-testid={`diff-card-${i}`} className="border-t border-line bg-panel/60 p-5">
                <p className="text-sm text-mute line-through decoration-mute/50">{left}</p>
                <p className="mt-2 font-display text-base font-semibold text-mist">{right}</p>
              </div>
            ))}
          </div>
          <div className="hidden md:block">
            {DIFF_ROWS.map(([left, right], i) => (
              <div key={i} data-testid={`diff-row-${i}`} className="grid grid-cols-2 border-t border-line bg-panel/60 transition-colors duration-300 hover:bg-panel2/60">
                <p className="px-6 py-5 text-sm text-mute">{left}</p>
                <p className="border-l border-line px-6 py-5 font-display text-sm font-semibold text-mist">{right}</p>
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.15} className="mt-12">
          <blockquote className="border-l-2 border-teal pl-6 font-display text-2xl sm:text-3xl font-semibold leading-snug tracking-tight text-mist" data-testid="differentiation-statement">
            "We're not building another payment app. We're building the intelligence and infrastructure around financial transactions."
          </blockquote>
        </Reveal>
      </div>
    </section>
  );
}

export function TechLine() {
  return (
    <p className="font-mono text-[11px] leading-relaxed tracking-[0.14em] text-mute/80" data-testid="tech-line">
      {TECH_LINE}
    </p>
  );
}
